import { inject, Injectable } from "@angular/core";
import { Offer } from "@models";
import { TRADE_TYPE } from "@models/trade-offer.model";
import { TradeBuyComponent } from "@modules/trade/modals/trade-buy-modal/trade-buy-modal.component";
import { Modal, ModalService } from "@core-services/modal.service";
import { FiatWalletService } from "@main-services/wallet/fiat_wallet.service";
import { AccountService } from "@modules/account/services/account.service";
import { TradableWalletListService } from "@main-services/wallet/tradable_wallet_list.service";
import { WalletSelectionModalComponent } from "@modules/main/components/tradable-to-view-item/tradable-to-wallet/wallet-selection-modal/wallet-selection-modal.component";
import { TradableWallet } from "@models/interfaces/tradable_wallet";
import { TradeSellComponent } from "@modules/trade/modals/trade-sell-modal/trade-sell.component";
import { UserFiatFundsService } from "@modules/shared/services/user-fiat-funds.service";
import { FiatDepositEntryService } from "@modules/+new/payments/features/fiat-deposit/infrastructure/fiat-deposit-entry.service";
import { convertTradableToFiat, toEuroByFiatId } from "@helpers/tradable-convert";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { FiatService } from "@main-services/currency";
import { TradableOfferService } from "@modules/trade/services/tradable-offer-service";

@Injectable({
	providedIn: "root"
})
export class LegacyTradeEntryService {
	private readonly modalService = inject(ModalService);
	private readonly fiatWalletService = inject(FiatWalletService);
	private readonly accountService = inject(AccountService);
	private readonly walletListService = inject(TradableWalletListService);
	private readonly fundsService = inject(UserFiatFundsService);
	private readonly fiatDepositEntryService = inject(FiatDepositEntryService);
	private readonly fiatService = inject(FiatService);
	private readonly tradableListService = inject(TradableListService);
	private readonly tradableOfferService = inject(TradableOfferService);

	public openLegacyBuy(isLimitOrder: boolean = false): void {
		const offer = new Offer();
		offer.setType(TRADE_TYPE.BUY);
		this.modalService.create(TradeBuyComponent, { data: offer, isLimitOrder: isLimitOrder }).present();
	}

	public openLegacyAmount(
		assetId: string,
		walletId: string,
		fiatId: string,
		fiatWalletId: string,
		headerSwitch?: boolean
	): void {
		if (this.fundsService.hasFiatWalletsWithFunds()) {
			const offer = new Offer();
			offer.setType(TRADE_TYPE.BUY);

			offer.setWalletId(walletId);
			offer.setDirectTradableWalletOffer(true);
			offer.setCurrencyId(assetId);
			offer.setIndexTrade(false);
			offer.setFiatId(fiatId);
			offer.setFiatWalletId(fiatWalletId);
			this.modalService.create(TradeBuyComponent, { data: offer, cameFromHeaderSwitch: headerSwitch }).present();
		} else {
			this.fiatDepositEntryService.openDeposit();
		}
	}

	public openLegacyBuyForAsset(assetId: string): void {
		const offer = new Offer();
		offer.setType(TRADE_TYPE.BUY);
		offer.setCurrencyId(assetId);
		let wallets = this.walletListService.getWalletsForTradable(assetId);
		const fiatId = this.accountService.account?._fiat_id;
		const fiatWalletId = this.fiatWalletService.getWalletByFiatId(fiatId)?._id;

		if (wallets.length > 1) {
			wallets = wallets.filter((w) => !w.isNew());
		}

		// If we still have more than 1 wallet, then we need to show the multiple wallets dialog
		if (wallets.length > 1) {
			const selectionModal: Modal<WalletSelectionModalComponent> = this.modalService.create(
				WalletSelectionModalComponent,
				{
					tradableWallets: wallets,
					tradableId: assetId
				}
			);
			selectionModal.present();
			selectionModal.onDismiss.subscribe((res) => {
				const selectedWallet: TradableWallet = res["data"];
				if (!selectedWallet) {
					return;
				}
				offer.setWalletId(selectedWallet.getId());
				offer.setDirectTradableWalletOffer(true);
				offer.setFiatId(fiatId);
				offer.setFiatWalletId(fiatWalletId);
				this.modalService.create(TradeBuyComponent, { data: offer }).present();
			});
		} else {
			offer.setWalletId(wallets[0].getId());
			offer.setDirectTradableWalletOffer(true);
			offer.setFiatId(fiatId);
			offer.setFiatWalletId(fiatWalletId);
			this.modalService.create(TradeBuyComponent, { data: offer }).present();
		}
	}

	public openSellNotification(assetId: string): void {
		const tradable = this.tradableListService.getItemById(assetId);
		const defaultFiat = this.accountService.getDefaultFiat();
		const hasSellBalance = this.walletListService.getWalletsByTradableId(assetId)?.some((wallet) => {
			if (!tradable) {
				return false;
			}
			const walletAmount = convertTradableToFiat(tradable, wallet.getBalance(), defaultFiat);
			return toEuroByFiatId(walletAmount, defaultFiat) >= tradable.getMinSell();
		});
		hasSellBalance ? this.openSellAmount(assetId) : this.openLegacySell(false);
	}

	public openLegacySell(isLimitOrder: boolean = false): void {
		const offer = new Offer();
		offer.setType(TRADE_TYPE.SELL);
		this.modalService
			.create(TradeSellComponent, { data: offer, isLimitOrder: isLimitOrder, fromTWFlow: true })
			.present();
	}

	public openSellAmount(
		assetId: string,
		walletId?: string,
		fiatId?: string,
		fiatWalletId?: string,
		amount?: string,
		headerSwitch?: boolean
	): void {
		const offer = new Offer();
		offer.setType(TRADE_TYPE.SELL);

		// If values are not passed use default values
		const defaultWallet = this.walletListService.getWalletsForTradable(assetId).find((w) => w.isDefault());
		const defaultFiat = this.accountService.getDefaultFiat();
		const defaultFiatWallet = this.fiatWalletService.getWalletByFiatId(defaultFiat?._id);

		offer.setWalletId(walletId ?? defaultWallet.getId());
		offer.setDirectTradableWalletOffer(true);
		offer.setCurrencyId(assetId);
		offer.setIndexTrade(false);
		offer.setFiatId(fiatId ?? defaultFiat.getId());
		offer.setFiatWalletId(fiatWalletId ?? defaultFiatWallet.getId());
		const convertedFiat = convertTradableToFiat(
			this.tradableListService.getItemById(assetId),
			+amount,
			this.fiatService.getItemById(fiatId)
		);
		offer.setAmountFiat(convertedFiat.toString() ?? "0");
		this.modalService.create(TradeSellComponent, { data: offer, cameFromHeaderSwitch: headerSwitch }).present();
	}

	public openSellHyperlink(assetId?: string): void {
		if (assetId) {
			const { offer } = this.tradableOfferService.generateTradableOffer(assetId);
			offer.setType(TRADE_TYPE.SELL);
			this.modalService.create(TradeSellComponent, { data: offer }).present();
		} else {
			this.openLegacySell();
		}
	}

	public openBuyHyperlink(assetId?: string): void {
		if (assetId) {
			this.openLegacyBuyForAsset(assetId);
		} else {
			this.openLegacyBuy();
		}
	}
}
